import {
  cn,
  Flex,
  Skeleton,
  Select,
  SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@suns/design-system";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import { StatsQueryParams } from "@suns/api/generated-client/apollo";
import { useApolloStats } from "./hooks/useApolloStats";
import { SunsApiError } from "@suns/api";
import { useEffect, useState } from "react";
import { StatName } from "@/shared/const";
import { StatsFormat, StatsGrouping, StatsObject, StatsType } from "./const";
import { DataTable } from "@suns/design-system/src/components/DataTable/DataTable";
import {
  advancedColumns,
  boxscoreColumns,
  gameColumns,
  seasonColumns,
} from "./table/columns";

const formatOptions = [
  { value: StatsFormat.TOTALS, label: StatName.Totals },
  { value: StatsFormat.PER_GAME, label: StatName.PerGame },
  {
    value: StatsFormat.PER_POSSESSION,
    label: StatName.PerPossession,
  },
  {
    value: StatsFormat.PER_100_POSSESSIONS,
    label: StatName.Per100Possessions,
  },
  { value: StatsFormat.PER_MINUTE, label: StatName.PerMinute },
  {
    value: StatsFormat.PER_40_MINUTES,
    label: `${StatName.Per40} Minutes`,
  },
  {
    value: StatsFormat.PER_36_MINUTES,
    label: `${StatName.Per36} Minutes`,
  },
];

const measureTypeOptions = [
  { value: StatsType.ADVANCED, label: StatName.Advanced },
  { value: StatsType.BOX_SCORE, label: StatName.BoxScore },
];

interface StatsTableProps {
  title?: string;
  object: StatsObject;
  grouping: StatsGrouping;
  params?: StatsQueryParams;
  careerRow?: boolean;
  lastNGamesSelect?: boolean;
  formatSelect?: boolean;
  measureSelect?: boolean;
  defaultMeasureType?: StatsType;
  size?: "sm" | "md";
}

export function ApolloStatsTable({
  title,
  object,
  grouping,
  params = {},
  careerRow = false,
  lastNGamesSelect = false,
  formatSelect = false,
  measureSelect = false,
  defaultMeasureType,
  size = "md",
}: StatsTableProps) {
  const [format, setFormat] = useState<StatsQueryParams.format>(
    StatsQueryParams.format.PER_GAME
  );
  const [limit, setLimit] = useState<string>(lastNGamesSelect ? "5" : "999");
  const [measureType, setMeasureType] = useState<StatsType>(
    defaultMeasureType ?? StatsType.BOX_SCORE
  );
  const [numRows, setNumRows] = useState<number>(0);

  const { response, loading, error } = useApolloStats(
    object,
    grouping,
    {
      ...params,
      format,
      types: [measureType],
      limit: Number(limit),
    },
    careerRow
  );

  useEffect(() => {
    if (response?.stats) {
      setNumRows(response.stats.length);
    }
  }, [response]);

  if (loading) {
    return (
      <SkeletonTable
        title={title}
        rows={numRows}
        selectControls={formatSelect || lastNGamesSelect || measureSelect}
      />
    );
  }

  if (error) {
    throw new SunsApiError("Unable to load stats.", {
      cause: error,
    });
  }

  const columns =
    measureType === StatsType.ADVANCED ? advancedColumns : boxscoreColumns;

  const leftColumns =
    grouping === StatsGrouping.GAME
      ? gameColumns
      : grouping === StatsGrouping.SEASON
        ? seasonColumns
        : [];

  const significantDigits = grouping === StatsGrouping.GAME ? 0 : 1;
  const hasSelectControls = measureSelect || formatSelect || lastNGamesSelect;

  return (
    <Flex direction="down" gap="sm">
      {title && (
        <Text heading muted>
          {title}
        </Text>
      )}
      {hasSelectControls && (
        <Flex gap="sm">
          {measureSelect ? (
            <Select
              value={measureType}
              onValueChange={(value) => setMeasureType(value as StatsType)}
              className="bg-white"
            >
              {measureTypeOptions.map((option) => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
          ) : null}
          {formatSelect && (
            <Select
              value={format}
              onValueChange={(value) =>
                setFormat?.(value as StatsQueryParams.format)
              }
              className="bg-white"
            >
              {formatOptions.map((option) => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
          )}
          {lastNGamesSelect && (
            <Select
              value={limit}
              onValueChange={(value) => setLimit?.(value)}
              className="bg-white"
            >
              {[
                { value: "0", label: "All Games" },
                { value: "5", label: "Last 5 Games" },
                { value: "20", label: "Last 20 Games" },
              ].map((option) => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
          )}
        </Flex>
      )}
      <DataTable
        data={response?.stats ?? []}
        columns={[...leftColumns, ...columns(significantDigits)]}
        className={
          size === "md"
            ? cn(
                "max-md:[&_td]:p-2",
                "max-md:[&_td]:text-sm",
                "max-md:[&_th]:p-2",
                "max-md:[&_th]:text-xs",
                "[&_th]:font-bold",
                "[&_th]:text-xs",
                "even:[&_tr]:bg-gray-50",
                "[&_tr]:text-xs",
                careerRow && "last:[&_tr]:font-bold"
              )
            : cn(
                "[&_th]:font-bold",
                "[&_th]:text-[9px]",
                "even:[&_tr]:bg-gray-50",
                "[&_tr]:text-[9px]",
                "[&_th]:px-1",
                "[&_th]:py-0",
                "[&_td]:p-1",
                careerRow && "last:[&_tr]:font-bold",
                "w-full"
              )
        }
      />
    </Flex>
  );
}

export function SkeletonTable({
  title,
  rows,
  selectControls,
}: {
  title?: string;
  rows: number;
  selectControls: boolean;
}) {
  return (
    <Flex direction="down" gap="md">
      <Text heading variant="muted">
        {title}
      </Text>
      {selectControls && <Skeleton className="h-10 w-1/3" />}
      <Table
        className={cn("rounded border", [
          "max-md:[&_td]:p-2",
          "max-md:[&_td]:text-sm",
          "max-md:[&_th]:p-2",
          "max-md:[&_th]:text-xs",
        ])}
      >
        <TableHeader>
          <TableRow>
            {Array.from({ length: 18 }, (_, i) => i + 1).map((_, index) => (
              <TableHead className="px-[0.85rem]" key={index}>
                <Skeleton className="h-4 w-full" />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }, (_, i) => i + 1).map((i) => (
            <TableRow key={`skeleton-row-${i}`}>
              {Array.from({ length: 18 }, (_, i) => i + 1).map((_, index) => (
                <TableCell
                  className={cn(index !== 0 && "border-l")}
                  key={index}
                >
                  <Skeleton className="h-4 w-10" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Flex>
  );
}
